import { useContext, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import Cookies from 'js-cookie';

import { Heading, Icon, Paragraph, Text, TextButton, UpsellBanner } from '@zillow/constellation-10';
import { IconExternalOutline, IconHomeTypeFilled, IconTourDoorFilled } from '@zillow/constellation-icons';

import { useClientAppConfigWebApiHandler_getFeature } from '@zg-rentals/rental-manager-env';

import TenantPlacementCSEvents from '../../model/ClickstreamEvent/TenantPlacementCSEvents';
import { RMWContext } from '../../providers/RMWContext';

import accessUtils from '../../utils/accessUtils';

import { useSelector } from '../../selectors/useSelector';

import { TENANT_PLACEMENT_COOKIES } from '../../constants/CookieConstants';
import { TENANT_PLACEMENT_ENTRY_POINTS } from '../TenantPlacement/TenantPlacement';

export const TenantPlacementUpsellBanner = () => {
  const { currentProperty, user, isProd } = useSelector((state) => {
    return {
      currentProperty: state.properties.currentProperty,
      user: state.user,
      isProd: state.app.isRunningInProd,
    };
  }, shallowEqual);

  const { data: enabledStates = '' } = useClientAppConfigWebApiHandler_getFeature({
    appName: 'zrm_web',
    featureName: 'tenantPlacementStates',
  });

  const { paths } = useContext(RMWContext);

  const canAccessTenantPlacementListingModuleBanner = accessUtils.canAccessTenantPlacementListingModuleBanner({
    currentProperty,
    user,
    enabledStates,
  });

  const tenantPlacementPageUrl = useMemo(() => {
    const url = new URL(paths.tenantPlacement, isProd ? 'https://www.zillow.com/' : 'https://www.qa.zillow.net/');
    url.searchParams.set(
      'from',
      canAccessTenantPlacementListingModuleBanner
        ? TENANT_PLACEMENT_ENTRY_POINTS.DASHBOARD_LISTING_BLOCK
        : TENANT_PLACEMENT_ENTRY_POINTS.DASHBOARD_BANNER,
    );
    url.searchParams.set('listingAlias', currentProperty.listingAlias);
    url.searchParams.set('state', currentProperty.state);
    return url;
  }, [currentProperty, canAccessTenantPlacementListingModuleBanner]);

  useEffect(() => {
    if (canAccessTenantPlacementListingModuleBanner !== null && canAccessTenantPlacementListingModuleBanner) {
      TenantPlacementCSEvents.TenantPlacementListingModuleUpsellBanner({ currentProperty, user });
    } else {
      TenantPlacementCSEvents.TenantPlacementDashboardUpsellBanner({ currentProperty, user });
    }
  }, [canAccessTenantPlacementListingModuleBanner]);

  const handleBannerClose = () => {
    Cookies.set(TENANT_PLACEMENT_COOKIES.HAS_CLOSED_BANNER, 'true', {
      expires: TENANT_PLACEMENT_COOKIES.EXPIRATION,
    });
  };

  const handleLearnMoreClick = () => {
    if (canAccessTenantPlacementListingModuleBanner) {
      TenantPlacementCSEvents.TenantPlacementListingModuleUpsellBannerLearnMore({ currentProperty, user });
    } else {
      TenantPlacementCSEvents.TenantPlacementDashboardUpsellBannerLearnMore({ currentProperty, user });
    }
  };

  return canAccessTenantPlacementListingModuleBanner ? (
    <UpsellBanner.Root
      data-eventid={TenantPlacementCSEvents.TenantPlacementListingModuleUpsellBanner.data_event_id}
      data-eventdescription="View tenant placement listing module upsell banner"
    >
      <UpsellBanner.Media css={{ alignSelf: 'start' }}>
        <Icon>
          <IconTourDoorFilled />
        </Icon>
      </UpsellBanner.Media>
      <UpsellBanner.Content>
        <Heading level={2} textStyle="body-lg-bold">
          Need help finding a tenant?
        </Heading>
        <div>
          <Paragraph>
            With full-service tenant placement, let the pros take care of listing, showing, and screening applicants.
            Only pay when a lease is signed.
          </Paragraph>
        </div>
        <TextButton
          asChild
          icon={<IconExternalOutline />}
          iconPosition="end"
          onClick={handleLearnMoreClick}
          data-eventid={TenantPlacementCSEvents.TenantPlacementListingModuleUpsellBannerLearnMore.data_event_id}
          css={{ alignSelf: 'start' }}
        >
          <a href={tenantPlacementPageUrl.toString()} target="_blank" rel="noreferrer">
            Learn more
          </a>
        </TextButton>
      </UpsellBanner.Content>
    </UpsellBanner.Root>
  ) : (
    <UpsellBanner
      media={
        <Icon>
          <IconHomeTypeFilled />
        </Icon>
      }
      header={
        <Heading level={2} textStyle="body-lg-bold">
          Need help finding a tenant?
        </Heading>
      }
      body={<Text>Get help from the professionals with our full-service tenant placement option.</Text>}
      action={
        <TextButton
          asChild
          icon={<IconExternalOutline />}
          iconPosition="end"
          onClick={handleLearnMoreClick}
          data-eventid={TenantPlacementCSEvents.TenantPlacementDashboardUpsellBannerLearnMore.data_event_id}
        >
          <a href={tenantPlacementPageUrl.toString()} target="_blank" rel="noreferrer">
            Learn more
          </a>
        </TextButton>
      }
      onOpenChange={(open) => !open && handleBannerClose()}
      data-eventid={TenantPlacementCSEvents.TenantPlacementDashboardUpsellBanner.data_event_id}
      data-eventdescription="View tenant placement dashboard upsell banner"
    />
  );
};

export default TenantPlacementUpsellBanner;
